import { useState, useCallback, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import CodeIcon from '@mui/icons-material/Code';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TranscriptView from './TranscriptView';
import { API_BASE_URL, getAuthHeaders } from '../auth';
import { useTranslation } from 'react-i18next';

const SPEAKER_COLORS = ['#19d0ef', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
const PAGE_SIZE = 5;

export default function SessionHistory({ activeSessionId }) {
  const { t } = useTranslation();

  // History panel state
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [page, setPage] = useState(0);

  // Selected session state (fully isolated from active recording)
  const [selectedId, setSelectedId] = useState(null);
  const [segments, setSegments] = useState([]);
  const [minutes, setMinutes] = useState(null);
  const [speakerNames, setSpeakerNames] = useState({});
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Speaker editing
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Title editing
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState('');

  // Menu anchors
  const [copyTranscriptAnchor, setCopyTranscriptAnchor] = useState(null);
  const [dlTranscriptAnchor, setDlTranscriptAnchor] = useState(null);
  const [copyMinutesAnchor, setCopyMinutesAnchor] = useState(null);
  const [dlMinutesAnchor, setDlMinutesAnchor] = useState(null);

  // Pagination
  const totalPages = Math.ceil(sessions.length / PAGE_SIZE);
  const pagedSessions = sessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const fetchSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/meeting/sessions`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.filter(s => s.status === 'completed' && s.id !== activeSessionId));
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoadingSessions(false);
      setSessionsLoaded(true);
    }
  }, [activeSessionId]);

  // Re-fetch when active session changes (new recording started/completed)
  useEffect(() => {
    if (open && sessionsLoaded) {
      fetchSessions();
    }
  }, [activeSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      fetchSessions();
    }
  };

  const handleSelect = async (session) => {
    if (session.id === selectedId) {
      setSelectedId(null);
      return;
    }
    setSelectedId(session.id);
    setSelectedTitle(session.title || null);
    setLoadingDetail(true);
    setSegments([]);
    setMinutes(null);
    setSpeakerNames(session.speaker_names || {});

    try {
      const headers = getAuthHeaders();
      const [transcriptRes, minutesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/meeting/${session.id}/transcript`, { headers }),
        fetch(`${API_BASE_URL}/api/meeting/${session.id}/minutes`, { headers }),
      ]);

      if (transcriptRes.ok) setSegments(await transcriptRes.json());
      if (minutesRes.ok) setMinutes(await minutesRes.json());
    } catch (err) {
      console.error('Failed to load session detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Speaker name helpers
  const detectedSpeakers = useMemo(() => {
    const ids = new Set();
    segments.forEach(seg => ids.add(seg.speaker_id));
    return [...ids].sort((a, b) => a - b);
  }, [segments]);

  const startEditing = (speakerId) => {
    setEditingSpeaker(speakerId);
    setEditValue(speakerNames[speakerId] || '');
  };

  const confirmEdit = () => {
    if (editingSpeaker !== null && editValue.trim()) {
      const updated = { ...speakerNames, [editingSpeaker]: editValue.trim() };
      setSpeakerNames(updated);
      if (selectedId) {
        fetch(`${API_BASE_URL}/api/meeting/${selectedId}/speakers`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(updated),
        }).catch(err => console.error('Failed to save speaker names:', err));
        // Update the session in the list so the label refreshes
        setSessions(prev => prev.map(s => s.id === selectedId ? { ...s, speaker_names: updated } : s));
      }
    }
    setEditingSpeaker(null);
    setEditValue('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') confirmEdit();
    if (e.key === 'Escape') { setEditingSpeaker(null); setEditValue(''); }
  };

  // Title editing
  const startEditingTitle = () => {
    setEditingTitle(true);
    setTitleEditValue(selectedTitle || '');
  };

  const confirmTitleEdit = () => {
    const trimmed = titleEditValue.trim();
    if (trimmed && trimmed !== selectedTitle) {
      setSelectedTitle(trimmed);
      if (selectedId) {
        fetch(`${API_BASE_URL}/api/meeting/${selectedId}/title`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: trimmed }),
        }).catch(err => console.error('Failed to save title:', err));
        // Update in sessions list
        setSessions(prev => prev.map(s => s.id === selectedId ? { ...s, title: trimmed } : s));
      }
    }
    setEditingTitle(false);
    setTitleEditValue('');
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') confirmTitleEdit();
    if (e.key === 'Escape') { setEditingTitle(false); setTitleEditValue(''); }
  };

  // Resolve speaker labels in text
  const resolveSpeakers = (text) => {
    if (!text) return text;
    return Object.entries(speakerNames).reduce(
      (t, [id, name]) => t.replace(new RegExp(`Speaker ${id}\\b`, 'g'), name),
      text
    );
  };

  // Format helpers
  const transcriptAsText = () =>
    segments.map(seg => {
      const name = speakerNames[seg.speaker_id] || `Speaker ${seg.speaker_id}`;
      return `${name}:\n${seg.text}`;
    }).join('\n\n');

  const transcriptAsMarkdown = () =>
    segments.map(seg => {
      const name = speakerNames[seg.speaker_id] || `Speaker ${seg.speaker_id}`;
      return `**${name}:**\n\n${seg.text}`;
    }).join('\n\n---\n\n');

  const minutesAsText = () => {
    if (!minutes) return '';
    const lines = [];
    if (minutes.summary) lines.push(`Summary\n\n${resolveSpeakers(minutes.summary)}`);
    if (minutes.topics?.length) lines.push(`Topics\n\n${minutes.topics.map(tp => `- ${resolveSpeakers(tp)}`).join('\n')}`);
    if (minutes.decisions?.length) lines.push(`Decisions\n\n${minutes.decisions.map(d => `- ${resolveSpeakers(d)}`).join('\n')}`);
    if (minutes.action_items?.length) {
      lines.push(`Action Items\n\n${minutes.action_items.map(a =>
        `- ${resolveSpeakers(a.task)}${a.owner ? ` (${resolveSpeakers(a.owner)})` : ''}${a.deadline ? ` — by ${a.deadline}` : ''}`
      ).join('\n')}`);
    }
    return lines.join('\n\n');
  };

  const minutesAsMarkdown = () => {
    if (!minutes) return '';
    const lines = [];
    if (minutes.summary) lines.push(`## Summary\n\n${resolveSpeakers(minutes.summary)}`);
    if (minutes.topics?.length) lines.push(`## Topics\n\n${minutes.topics.map(tp => `- ${resolveSpeakers(tp)}`).join('\n')}`);
    if (minutes.decisions?.length) lines.push(`## Decisions\n\n${minutes.decisions.map(d => `- ${resolveSpeakers(d)}`).join('\n')}`);
    if (minutes.action_items?.length) {
      lines.push(`## Action Items\n\n${minutes.action_items.map(a =>
        `- ${resolveSpeakers(a.task)}${a.owner ? ` (${resolveSpeakers(a.owner)})` : ''}${a.deadline ? ` — by ${a.deadline}` : ''}`
      ).join('\n')}`);
    }
    return lines.join('\n\n');
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAsPdf = async () => {
    if (!selectedId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/meeting/${selectedId}/pdf`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `meeting-minutes-${selectedId}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF download error:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getSpeakerList = (session) => {
    if (session.speaker_names && Object.keys(session.speaker_names).length > 0) {
      // Return entries sorted by speaker ID, with the display name as value
      return Object.entries(session.speaker_names)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, name]) => name);
    }
    return [];
  };

  const getDisplayTitle = (session) => {
    if (session.title) return session.title;
    // Fallback: build from speakers + date
    const speakers = getSpeakerList(session);
    const date = formatDateShort(session.started_at);
    if (speakers.length > 0) {
      return `${speakers.join(', ')} — ${date}`;
    }
    return formatDate(session.started_at);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1 }} />
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          py: 0.5,
          px: 0.5,
          borderRadius: '6px',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" sx={{
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          flexGrow: 1,
        }}>
          {t('mm_history') || 'History'}
          {sessionsLoaded && sessions.length > 0 && (
            <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5, fontWeight: 400 }}>
              ({sessions.length})
            </Typography>
          )}
        </Typography>
        {loadingSessions && <CircularProgress size={14} />}
        {open ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      </Box>

      <Collapse in={open}>
        {sessions.length === 0 && sessionsLoaded && !loadingSessions && (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 1, px: 0.5 }}>
            {t('mm_no_history') || 'No completed sessions yet.'}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
          {pagedSessions.map(s => {
            const speakers = getSpeakerList(s);
            const isSelected = selectedId === s.id;
            return (
              <Box
                key={s.id}
                onClick={() => handleSelect(s)}
                sx={{
                  p: 1.5,
                  borderRadius: '8px',
                  border: 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'action.selected' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: '13px',
                  lineHeight: 1.4,
                  mb: 0.5,
                }}>
                  {getDisplayTitle(s)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {speakers.map((name, i) => (
                      <Chip
                        key={i}
                        label={name}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '11px',
                          fontWeight: 500,
                          bgcolor: `${SPEAKER_COLORS[i % SPEAKER_COLORS.length]}18`,
                          color: SPEAKER_COLORS[i % SPEAKER_COLORS.length],
                          border: 'none',
                        }}
                        icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: SPEAKER_COLORS[i % SPEAKER_COLORS.length], ml: '5px !important' }} />}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px', flexShrink: 0, ml: 1 }}>
                    {formatDateShort(s.started_at)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 0.5, mb: 1 }}>
            <Button
              size="small"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              sx={{ minWidth: 0, px: 1, fontSize: '12px', textTransform: 'none' }}
            >
              {t('mm_prev') || 'Prev'}
            </Button>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {page + 1} / {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              sx={{ minWidth: 0, px: 1, fontSize: '12px', textTransform: 'none' }}
            >
              {t('mm_next') || 'Next'}
            </Button>
          </Box>
        )}

        {/* Selected session detail */}
        <Collapse in={selectedId !== null}>
          {loadingDetail && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {!loadingDetail && selectedId && (
            <Box sx={{ mt: 1 }}>
              {/* Editable title */}
              {selectedTitle && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                  {editingTitle ? (
                    <>
                      <TextField
                        size="small"
                        value={titleEditValue}
                        onChange={(e) => setTitleEditValue(e.target.value)}
                        onKeyDown={handleTitleKeyDown}
                        onBlur={confirmTitleEdit}
                        autoFocus
                        fullWidth
                        sx={{
                          '& .MuiInputBase-input': { fontSize: '14px', py: '4px', px: '8px', fontWeight: 600 },
                          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                        }}
                      />
                      <CheckIcon
                        sx={{ fontSize: 18, color: 'success.main', cursor: 'pointer', flexShrink: 0 }}
                        onClick={confirmTitleEdit}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '14px', flexGrow: 1 }}>
                        {selectedTitle}
                      </Typography>
                      <IconButton size="small" onClick={startEditingTitle} sx={{ p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5, '&:hover': { opacity: 1 } }} />
                      </IconButton>
                    </>
                  )}
                </Box>
              )}

              {/* Speaker pills */}
              {detectedSpeakers.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, alignItems: 'center' }}>
                  {detectedSpeakers.map(id => {
                    const color = SPEAKER_COLORS[id % SPEAKER_COLORS.length];
                    const label = speakerNames[id] || `Speaker ${id}`;
                    const isEditing = editingSpeaker === id;

                    if (isEditing) {
                      return (
                        <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                          <TextField
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            onBlur={confirmEdit}
                            autoFocus
                            sx={{
                              '& .MuiInputBase-input': { fontSize: '12px', py: '3px', px: '8px', width: '100px' },
                              '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                            }}
                          />
                          <CheckIcon
                            sx={{ fontSize: 16, color: 'success.main', cursor: 'pointer' }}
                            onClick={confirmEdit}
                          />
                        </Box>
                      );
                    }

                    return (
                      <Chip
                        key={id}
                        label={label}
                        size="small"
                        onClick={() => startEditing(id)}
                        onDelete={() => startEditing(id)}
                        deleteIcon={<EditIcon sx={{ fontSize: '13px !important', color: `${color} !important`, opacity: 0.6, '&:hover': { opacity: 1 } }} />}
                        sx={{
                          bgcolor: `${color}18`,
                          color: color,
                          fontSize: '12px',
                          fontWeight: 500,
                          height: 26,
                          border: 'none',
                          '& .MuiChip-icon': { color },
                        }}
                        icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, ml: '6px !important' }} />}
                      />
                    );
                  })}
                </Box>
              )}

              {/* Transcript */}
              {segments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                      {t('mm_transcript') || 'Transcript'}
                    </Typography>
                    <Tooltip title={t('mm_copy') || 'Copy'}>
                      <IconButton size="small" onClick={e => setCopyTranscriptAnchor(e.currentTarget)} sx={{ ml: 0.5, p: 0.25 }}>
                        <ContentCopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                    <Menu anchorEl={copyTranscriptAnchor} open={Boolean(copyTranscriptAnchor)} onClose={() => setCopyTranscriptAnchor(null)}>
                      <MenuItem onClick={() => { copyToClipboard(transcriptAsText()); setCopyTranscriptAnchor(null); }}>
                        <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Plain text</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { copyToClipboard(transcriptAsMarkdown()); setCopyTranscriptAnchor(null); }}>
                        <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Markdown</ListItemText>
                      </MenuItem>
                    </Menu>
                    <Tooltip title={t('mm_download') || 'Download'}>
                      <IconButton size="small" onClick={e => setDlTranscriptAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                        <DownloadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                    <Menu anchorEl={dlTranscriptAnchor} open={Boolean(dlTranscriptAnchor)} onClose={() => setDlTranscriptAnchor(null)}>
                      <MenuItem onClick={() => { downloadFile(transcriptAsText(), 'transcript.txt', 'text/plain'); setDlTranscriptAnchor(null); }}>
                        <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Plain text (.txt)</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { downloadFile(transcriptAsMarkdown(), 'transcript.md', 'text/markdown'); setDlTranscriptAnchor(null); }}>
                        <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Markdown (.md)</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { downloadAsPdf(); setDlTranscriptAnchor(null); }}>
                        <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>PDF</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                  <TranscriptView
                    segments={segments}
                    partialText=""
                    speakerNames={speakerNames}
                  />
                </Box>
              )}

              {/* Minutes */}
              {minutes && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                      {t('meeting_minutes') || 'Meeting Minutes'}
                    </Typography>
                    <Tooltip title={t('mm_copy') || 'Copy'}>
                      <IconButton size="small" onClick={e => setCopyMinutesAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                        <ContentCopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                    <Menu anchorEl={copyMinutesAnchor} open={Boolean(copyMinutesAnchor)} onClose={() => setCopyMinutesAnchor(null)}>
                      <MenuItem onClick={() => { copyToClipboard(minutesAsText()); setCopyMinutesAnchor(null); }}>
                        <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Plain text</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { copyToClipboard(minutesAsMarkdown()); setCopyMinutesAnchor(null); }}>
                        <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Markdown</ListItemText>
                      </MenuItem>
                    </Menu>
                    <Tooltip title={t('mm_download') || 'Download'}>
                      <IconButton size="small" onClick={e => setDlMinutesAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                        <DownloadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                    <Menu anchorEl={dlMinutesAnchor} open={Boolean(dlMinutesAnchor)} onClose={() => setDlMinutesAnchor(null)}>
                      <MenuItem onClick={() => { downloadFile(minutesAsText(), 'meeting-minutes.txt', 'text/plain'); setDlMinutesAnchor(null); }}>
                        <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Plain text (.txt)</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { downloadFile(minutesAsMarkdown(), 'meeting-minutes.md', 'text/markdown'); setDlMinutesAnchor(null); }}>
                        <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Markdown (.md)</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { downloadAsPdf(); setDlMinutesAnchor(null); }}>
                        <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>PDF</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: '8px',
                    border: 1, borderColor: 'divider' }}>
                    {minutes.summary && (
                      <Typography variant="body2" sx={{ mb: 2 }}>{resolveSpeakers(minutes.summary)}</Typography>
                    )}

                    {minutes.topics?.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('mm_topics') || 'Topics'}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {minutes.topics.map((topic, i) => <Chip key={i} label={resolveSpeakers(topic)} size="small" variant="outlined" />)}
                        </Box>
                      </Box>
                    )}

                    {minutes.decisions?.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('mm_decisions') || 'Decisions'}</Typography>
                        {minutes.decisions.map((d, i) => (
                          <Typography key={i} variant="body2" sx={{ ml: 1, mt: 0.5 }}>• {resolveSpeakers(d)}</Typography>
                        ))}
                      </Box>
                    )}

                    {minutes.action_items?.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('mm_action_items') || 'Action Items'}</Typography>
                        {minutes.action_items.map((a, i) => (
                          <Typography key={i} variant="body2" sx={{ ml: 1, mt: 0.5 }}>
                            • {resolveSpeakers(a.task)}{a.owner ? ` (${resolveSpeakers(a.owner)})` : ''}{a.deadline ? ` — by ${a.deadline}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Collapse>
      </Collapse>
    </Box>
  );
}
